import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { DatePicker } from '@/components/core/date-picker/date-picker';
import { Input } from '@/components/ui-kit/input';
import { Textarea } from '@/components/ui-kit/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { useAuthStore } from '@/state/store/auth';
import { orgDayStartToUtc } from '@/lib/utils/custom-date/custom-date';
import { useCreateFine } from '../../hooks/use-profile-view-lookups';
import { useTranslatedResolver } from '../employee-form/translate-form-errors';
import { fineFormSchema, FineFormValues } from '../../schemas/fine-form-schema';
import { Employee } from '../../types/employee.types';
import { useReportUnreportedError } from '@/state/query-client/hooks';

export interface FineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The employee being fined; the dialog renders nothing meaningful without one, so callers
   * should only ever open it with an employee already picked. */
  employee: Employee | null;
}

const DEFAULT_VALUES: FineFormValues = { FineType: '', FineAmount: 0, Date: '', Reason: '' };

/**
 * Lets an HR admin issue an ad-hoc lunch or non-compliance fine to an employee from the
 * directory list (legacy `HrFineComponent`, opened from its row action menu). The attendance
 * fine is computed automatically from approved correction requests, so it has no form here.
 */
export const FineDialog = ({ open, onOpenChange, employee }: FineDialogProps) => {
  const { t } = useTranslation();
  const finedBy = useAuthStore((state) => state.user?.itemId);
  const { mutateAsync: createFine, isPending } = useCreateFine();
  const reportError = useReportUnreportedError();

  const resolver = useTranslatedResolver<FineFormValues>(fineFormSchema);
  const form = useForm<FineFormValues>({
    resolver,
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    if (!open) return;
    form.reset(DEFAULT_VALUES);
  }, [open, form]);

  const handleSubmit = async (values: FineFormValues) => {
    if (!employee) return;
    try {
      await createFine({
        userId: employee.UserId ?? '',
        finedBy: finedBy ?? '',
        fineType: values.FineType as 'LunchFine' | 'NonComplianceFine',
        fineAmount: values.FineAmount,
        // The picked day as the instant it starts in the app timezone, so it reads back as that day.
        fineCreatedDateUtc: orgDayStartToUtc(values.Date) ?? new Date().toISOString(),
        remarks: values.Reason,
      });
      onOpenChange(false);
    } catch (error) {
      reportError(error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" alignCloseWithTitle>
        <DialogHeader>
          <DialogTitle>{t('CREATE_FINE')}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="FineType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('FINE_TYPE')}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('FINE_TYPE')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="LunchFine">{t('LUNCH')}</SelectItem>
                      <SelectItem value="NonComplianceFine">{t('NON_COMPLIANCE')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="FineAmount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('AMOUNT')}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        step="1"
                        placeholder={t('TYPE_AMOUNT')}
                        {...field}
                        onChange={(e) => field.onChange(e.target.valueAsNumber)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="Date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('DATE')}</FormLabel>
                    <FormControl>
                      <DatePicker
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                        placeholder={t('SELECT_DATE')}
                        className="w-full"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="Reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('REASON')}</FormLabel>
                  <FormControl>
                    <Textarea rows={4} maxLength={100} placeholder={t('REASON')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <DialogCancelButton onClick={() => onOpenChange(false)}>
                {t('CANCEL')}
              </DialogCancelButton>
              <Button type="submit" loading={isPending} disabled={isPending}>
                {t('SUBMIT')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
